import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, Icon, cn } from "@nexus/ui";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import type {
  PreflightMarket,
  PreflightReportDetail,
} from "../../types/preflight.types";
import { reportMarketsLabel } from "../../lib/market-label";
import { formatReportContext, formatReportTimestamp } from "../../lib/report-meta";
import { ChangeReportMarketDialog } from "../ChangeReportMarketDialog/ChangeReportMarketDialog";
import { MarketIcon } from "../MarketIcon/MarketIcon";
import { PlatformIcon } from "../PlatformIcon/PlatformIcon";
import { ReportBackLink } from "../ReportBackLink/ReportBackLink";
import { ReportPageActions } from "../ReportPageActions/ReportPageActions";
import { ReportSummaryMetrics } from "../ReportSummaryMetrics/ReportSummaryMetrics";

export interface ReportPageHeaderProps {
  report: PreflightReportDetail;
  showExport: boolean;
  showBackLink?: boolean;
  onBack: () => void;
  onDeleted?: () => void;
}

/** Report identity, actions, and inline aggregates in one cohesive header block. */
export function ReportPageHeader({
  report,
  showExport,
  showBackLink = true,
  onBack,
  onDeleted,
}: ReportPageHeaderProps) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const [isMarketDialogOpen, setIsMarketDialogOpen] = useState(false);
  const canChangeMarket = permissions.PREFLIGHT_RENAME_REPORT.isOn;

  const reportContext = formatReportContext({
    platform: t(`preflight.platform.${report.platform}`),
    placement: t(`preflight.placement.${report.placement}`),
  });
  /** Reports with no joins show the same placeholder the list column uses. */
  const markets = report.markets;
  const firstMarket = markets[0];
  const marketName = reportMarketsLabel(
    markets,
    t("preflight.markets.none", "No market"),
  );
  const createdAt = formatReportTimestamp(report.createdAt);
  const byline = report.createdBy
    ? t("preflight.report.generatedByOn", {
        name: report.createdBy.displayName,
        date: createdAt,
      })
    : t("preflight.report.generatedOn", { date: createdAt });

  return (
    <section
      className="flex flex-col gap-3"
      aria-labelledby="preflight-report-title"
    >
      {showBackLink ? <ReportBackLink onBack={onBack} /> : null}

      <Card className="border-border gap-0 overflow-hidden py-0">
        <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <h1
              id="preflight-report-title"
              className="text-foreground truncate text-xl font-semibold tracking-tight"
            >
              {report.name}
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              <span className="flex min-w-0 flex-col">
                <span className="inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap">
                  <span className="text-foreground max-w-64 truncate font-medium">
                    {report.brandName}
                  </span>
                  <span aria-hidden>{t("preflight.middot")}</span>
                  <PlatformIcon platform={report.platform} />
                  <span>{reportContext}</span>
                </span>
                <span className="my-[5px] min-w-0 max-w-full">
                  {canChangeMarket ? (
                    <button
                      type="button"
                      onClick={() => setIsMarketDialogOpen(true)}
                      className={cn(
                        "hover:text-foreground inline-flex min-w-0 max-w-full cursor-pointer items-start gap-1.5 rounded-sm text-left transition-colors duration-200",
                        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                      )}
                      aria-label={t(
                        "preflight.changeMarket.triggerAria",
                        "Change market: {{market}}",
                        { market: marketName },
                      )}
                    >
                      <ReportMarketsLabel
                        firstMarket={firstMarket}
                        marketName={marketName}
                        showEditIcon
                      />
                    </button>
                  ) : (
                    <span className="inline-flex min-w-0 max-w-full items-start gap-1.5">
                      <ReportMarketsLabel
                        firstMarket={firstMarket}
                        marketName={marketName}
                      />
                    </span>
                  )}
                </span>
              </span>
            </p>
            <p className="text-muted-foreground/80 mt-1 text-xs">{byline}</p>
          </div>
          <ReportPageActions
            report={report}
            showExport={showExport}
            onDeleted={onDeleted}
          />
        </div>

        <ReportSummaryMetrics
          report={report}
          className="border-border bg-muted/70 rounded-none border-t px-5 py-6 sm:px-8 sm:py-7"
        />
      </Card>

      {canChangeMarket && isMarketDialogOpen ? (
        <ChangeReportMarketDialog
          report={report}
          open={isMarketDialogOpen}
          onOpenChange={setIsMarketDialogOpen}
          trigger={null}
        />
      ) : null}
    </section>
  );
}

/**
 * Icon + wrapping name list. The icon stays on the first line; names wrap as a
 * left-aligned block so a long join list does not drift under the globe.
 */
function ReportMarketsLabel({
  firstMarket,
  marketName,
  showEditIcon = false,
}: {
  firstMarket: PreflightMarket | undefined;
  marketName: string;
  showEditIcon?: boolean;
}) {
  return (
    <>
      {firstMarket ? (
        <MarketIcon
          kind={firstMarket.kind}
          code={firstMarket.code}
          name={firstMarket.name}
          className="mt-0.5"
        />
      ) : null}
      <span className="min-w-0">
        {marketName}
        {showEditIcon ? (
          <Icon
            name="pencil"
            size="xs"
            className="text-muted-foreground ml-1.5 inline-block align-text-bottom"
            aria-hidden
          />
        ) : null}
      </span>
    </>
  );
}
